import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';

import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Textarea } from '@/components/ui/textarea';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import {
	useEntityForm,
	type EntityFormProps,
	type EntityFormSubmitHandler
} from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

const dateField = (label: string) =>
	z
		.string({ required_error: `${label} is required` })
		.trim()
		.min(1, `${label} is required`)
		.refine(value => !Number.isNaN(new Date(value).getTime()), {
			message: `${label} must be a valid date`
		});

const campaignFormSchema = z
	.object({
		name: z
			.string({ required_error: 'Campaign name is required' })
			.trim()
			.min(1, 'Campaign name is required'),
		description: z
			.string()
			.optional()
			.default('')
			.transform(value => (value ?? '').trim()),
		startDate: dateField('Start date'),
		endDate: dateField('End date'),
		isActive: z.boolean()
	})
	.superRefine((values, ctx) => {
		const start = new Date(values.startDate).getTime();
		const end = new Date(values.endDate).getTime();

		if (!Number.isNaN(start) && !Number.isNaN(end) && start > end) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['endDate'],
				message: 'End date must be later than the start date'
			});
		}
	});

export type CampaignFormValues = z.infer<typeof campaignFormSchema>;

export interface CampaignFormSubmitData {
	campaignId?: string;
	name: string;
	description: string;
	startDate: string;
	endDate: string;
	isActive: boolean;
}

export interface CampaignFormProps
	extends Omit<
		EntityFormProps<CampaignFormValues, CampaignFormSubmitData>,
		'onSubmit'
	> {
	onSubmit: EntityFormSubmitHandler<CampaignFormSubmitData>;
	headerTitle: string;
	onBack?: () => void;
}

const toLocalDateInputValue = (isoValue?: string | null) => {
	if (!isoValue) {
		return '';
	}

	const date = new Date(isoValue);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	const tzOffset = date.getTimezoneOffset() * 60000;
	const local = new Date(date.getTime() - tzOffset);
	return local.toISOString().slice(0, 10);
};

const toIsoString = (value: string) => {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const buildInitialValues = (
	data?: CampaignFormSubmitData
): CampaignFormValues | undefined => {
	if (!data) {
		return undefined;
	}

	return {
		name: data.name ?? '',
		description: data.description ?? '',
		startDate: toLocalDateInputValue(data.startDate),
		endDate: toLocalDateInputValue(data.endDate),
		isActive: data.isActive ?? true
	};
};

export const CampaignForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	headerTitle,
	onBack
}: CampaignFormProps) => {
	const { t } = useTranslation('campaigns');
	const nameInputRef = useRef<HTMLInputElement>(null);
	const memoizedInitialData = useMemo(
		() => buildInitialValues(initialData),
		[initialData]
	);

	const form = useEntityForm<CampaignFormValues>({
		mode,
		schema: campaignFormSchema,
		defaultValues: {
			name: '',
			description: '',
			startDate: '',
			endDate: '',
			isActive: true
		},
		initialData: memoizedInitialData
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	// Auto-focus name field when creating a new campaign
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				nameInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	const watchedValues = form.watch();

	const canSubmit = useMemo(() => {
		return Boolean(
			watchedValues.name?.trim() &&
				watchedValues.startDate &&
				watchedValues.endDate &&
				Object.keys(form.formState.errors).length === 0
		);
	}, [
		watchedValues.name,
		watchedValues.startDate,
		watchedValues.endDate,
		form.formState.errors
	]);

	const handleFormSubmit: EntityFormSubmitHandler<
		CampaignFormValues
	> = values => {
		const submitData: CampaignFormSubmitData = {
			name: values.name.trim(),
			description: values.description.trim(),
			startDate: toIsoString(values.startDate),
			endDate: toIsoString(values.endDate),
			isActive: values.isActive
		};

		// Add campaignId if in edit mode
		if (mode === 'edit' && initialData && 'campaignId' in initialData) {
			submitData.campaignId = (initialData as any).campaignId;
		}

		onSubmit(submitData);
	};

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						disabled={!canSubmit}
						validationErrors={form.formState.errors}
						formId="campaign-form"
					/>
				}
			/>

			<Form {...form}>
			<form
				id="campaign-form"
				onSubmit={form.handleSubmit(handleFormSubmit)}
				className="space-y-6"
			>
				<Card className="space-y-6">
					<div>
						<h3 className="text-lg font-semibold">
							{t('form.campaignInformation')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{t('form.campaignInformationDesc')}
						</p>
					</div>

					<div className="space-y-4">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.campaignName')} *
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											ref={nameInputRef}
											placeholder={t(
												'form.campaignNamePlaceholder'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.description')}
									</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											placeholder={t(
												'form.descriptionPlaceholder'
											)}
											rows={4}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>
				</Card>

				<Card className="space-y-4">
					<div>
						<h3 className="text-lg font-semibold">
							{t('form.scheduling')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{t('form.schedulingDesc')}
						</p>
					</div>{' '}
					<div className="grid gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="startDate"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.startDate')} *
									</FormLabel>
									<FormControl>
										<DateInput value={field.value} onChange={field.onChange} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="endDate"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.endDate')} *</FormLabel>
									<FormControl>
										<DateInput value={field.value} onChange={field.onChange} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>
			</form>
		</Form>
		</>
	);
};
